import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	input,
	model,
	OnDestroy,
	untracked,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
	CourseSearchBarComponent,
	MainHeaderComponent,
	UniversitiesSkeletonComponent,
} from '@web/shared/ui';
import { CourseSearchFilterComponent } from '../../components/filter/course-search-filter/course-search-filter.component';
import { CourseSearchResultComponent } from '../../components/course-search-result/course-search-result.component';
import { CourseSearchPaginationComponent } from '../../components/course-search-pagination/course-search-pagination.component';
import {
	getStateCourseSearchClearingUni,
	getStateCourseSearchFilterPills,
	getStateCourseSearchIsLoading,
	getStateCourseSearchIsPaginationLoading,
	getStateUniversityCourses,
} from '../../data-access/+store/course-search.selectors';
import { Store } from '@ngrx/store';
import {
	CourseSearchLocationGAEnum,
	DegreeLevelLabel,
	DegreeLevelOption,
	Offset,
} from '@uc/web/shared/data-models';
import { CourseListUniComponent } from '../../components/course-list-uni/course-list-uni.component';
import {
	fetchClearingUniCourses,
	fetchPaginatedClearingUniCourses,
	fetchPaginatedUniCourses,
	fetchUniCourses,
	updateClearingUniCourseSearchPayload,
	updateUniCourseSearchPayload,
} from '../../data-access/+store/course-search.actions';
import { Router } from '@angular/router';
import {
	queryParamsToObject,
	sortQueryParamsByKey,
} from '../../utils/query-params.utils';
import { SeoMetadataService } from '@uc/utilities';
import {
	getClearingHeaderDescription,
	getClearingMetadataDescription,
	getHeaderDescription,
	getMetadataDescription,
} from '../../utils/university-course-search-copy.utils';
import { Meta } from '@angular/platform-browser';
import { getRouteSegmentsAndQueryParams } from '../../utils/route.utils';
import {
	CLE_UNI_SEARCH_SLUG,
	DegreeLevelService,
	MAX_UCAS_TARIFF,
} from '@uc/web/shared/data-access';
import { formatTitleCaseString, getDegreeLevelOptions } from '../../utils/utils';
import {
	getDegreeLevelType,
	getUniSearchFilterResetPayload,
	getStudyModeType,
} from '../../utils/course-search-payload.utils';
import { UniversityCourseSearchPayload } from '../../models/course-search-payload.models';
import { FilterTogglerService, NA } from '@uc/web/core';
import { FilterSortWidgetComponent } from '@uc/web/shared/feature/feature-filter';
import { Product, ProductEnum } from '@uc/shared/data-models';
import { CourseListClearingUniComponent } from '../../components/clearing/course-list-clearing-uni/course-list-clearing-uni.component';
import {
	isPartTimeCourse,
	isValidClearingDegreeQueryParam,
	removeClearingFilters,
} from '../../utils/clearing.utils';
import { RemovedFiltersInfoService } from '../../components/clearing/removed-filters-info/removed-filters-info.service';
import { UniSearchSlug } from '../../models/single-uni-course-search.models';

@Component({
	selector: 'uc-university-course-search',
	templateUrl: './university-course-search.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MainHeaderComponent,
		CourseSearchFilterComponent,
		UniversitiesSkeletonComponent,
		CourseSearchResultComponent,
		CourseSearchPaginationComponent,
		CourseListUniComponent,
		CourseSearchBarComponent,
		FilterSortWidgetComponent,
		CourseListClearingUniComponent,
	],
})
export class UniversityCourseSearchComponent implements OnDestroy {
	level = input.required<UniSearchSlug>();
	uni = input.required<string>();
	mode = input<string>();
	search = input<string>('');
	ucaspts = input<string>();
	page = model<string>();

	studyLevel = computed<Product>(() => {
		if (this.level() === CLE_UNI_SEARCH_SLUG) {
			return 'clearing';
		} else return this.level() as Product;
	});

	isClearingView = computed(() => this.studyLevel() === ProductEnum.Clearing);

	ugPgUni = this._store.selectSignal(getStateUniversityCourses);
	cleUni = this._store.selectSignal(getStateCourseSearchClearingUni);
	university = computed(() => (this.isClearingView() ? this.cleUni() : this.ugPgUni()));

	isLoading = this._store.selectSignal(getStateCourseSearchIsLoading);
	isPaginationLoading = this._store.selectSignal(
		getStateCourseSearchIsPaginationLoading,
	);

	formattedSubject = computed(() => this.search()?.replace(/-/g, ' '));

	headerTitle = computed(() => {
		const uniName = this.university()?.name;

		return this.isClearingView()
			? `${uniName} ${formatTitleCaseString(this.search())} Clearing courses`
			: `${uniName} ${formatTitleCaseString(this.search() || this.studyLevel())} courses`;
	});

	headerDescription = computed(() => {
		const uniName = this.university()?.name;
		if (!uniName) return '';

		return this.isClearingView()
			? getClearingHeaderDescription(this.currentYear, uniName)
			: getHeaderDescription(this.studyLevel(), uniName, this.formattedSubject());
	});

	filterPillsCount = computed(() => {
		const pills = this._store.selectSignal(getStateCourseSearchFilterPills);
		return pills().length;
	});

	showLoadMore = computed(
		() =>
			this.university()?.courses?.data?.length &&
			!!this.university()?.courses?.has_more_pages,
	);

	degreeLevelOptions!: DegreeLevelOption[];

	readonly na = NA;
	headerSearchBar = CourseSearchLocationGAEnum.HeaderSearchBar;
	currentYear = new Date().getFullYear();

	private _metadataEffect = effect(() => {
		const uniName = this.university()?.name;
		if (!uniName) return;

		const subject = this.search() ?? '';
		const title = formatTitleCaseString(
			this.isClearingView()
				? `${uniName} ${subject} Clearing courses`
				: `${uniName} ${subject || this.studyLevel()} Courses`,
		);

		const description = this.isClearingView()
			? getClearingMetadataDescription(this.currentYear, uniName)
			: getMetadataDescription(this.studyLevel(), uniName, this.formattedSubject());

		this._seoSrv.updateTitle(title);
		this._seoSrv.updateDescription(description ?? '');
		this._meta.updateTag({ name: 'robots', content: 'noindex' });
	});

	private _levelEffect = effect(() => {
		this.level();
		untracked(() => {
			this._initPayload();
			this._initDegreeLevel();
		});
	});

	constructor(
		public filterTogglerSrv: FilterTogglerService,
		private _meta: Meta,
		private _store: Store,
		private _router: Router,
		private _location: Location,
		private _seoSrv: SeoMetadataService,
		private _degreeLevelSrv: DegreeLevelService,
		private _removedFiltersInfoSrv: RemovedFiltersInfoService,
	) {}

	ngOnDestroy(): void {
		this._metadataEffect.destroy();
		this._levelEffect.destroy();
	}

	onFilterReset() {
		const payload = getUniSearchFilterResetPayload(this.uni(), this.studyLevel());
		this.updatePayloadAndFetchCourses(payload);
	}

	onSearch(autosuggest: { searchTerm: string; degree?: string }) {
		const { searchTerm = '', degree = this.studyLevel() } = autosuggest;
		const payload = this._updateUniCourseSearchPayload(searchTerm, degree);
		this.updatePayloadAndFetchCourses(payload);
		this._navigateWithSearch(searchTerm, degree);
	}

	onStudyLevelChange(studyLevel: string) {
		const route = this._router.url.split('?');
		const queryParams = queryParamsToObject(route[1]);
		const isCle = studyLevel === ProductEnum.Clearing;
		const subPath = isCle ? CLE_UNI_SEARCH_SLUG : studyLevel;

		if (isCle) {
			this._removeInvalidQueryParams(queryParams);
			this._removedFiltersInfoSrv.setRemovedFilters(
				removeClearingFilters(this._router.url),
			);
		} else {
			this._removedFiltersInfoSrv.resetRemovedFilters();
		}

		this._router.navigate(['/courses', subPath], { queryParams });
	}

	onLoadMore(page: number) {
		const payload = { page, offset: Offset.On };
		this._updatePayloadAndFetchPaginatedCourses(payload);

		const route = this._router.url.split('?');
		const queryParamsString = route[1] ? `${route[1]}&page=${page}` : `page=${page}`;
		const queryParamsObject = queryParamsToObject(queryParamsString);

		if (queryParamsObject) {
			const sortedQueryParams = sortQueryParamsByKey(queryParamsObject);

			this.page.set(page.toString());

			const queryParams = new URLSearchParams(sortedQueryParams).toString();

			this._location.go(route[0], `?${queryParams}`);
		}
	}

	updatePayloadAndFetchCourses(payload: Partial<UniversityCourseSearchPayload>) {
		this._updatePayload(payload);
		this.isClearingView()
			? this._store.dispatch(fetchClearingUniCourses())
			: this._store.dispatch(fetchUniCourses());
	}

	private _updatePayload(payload: Partial<UniversityCourseSearchPayload>) {
		this.isClearingView()
			? this._store.dispatch(updateClearingUniCourseSearchPayload({ payload }))
			: this._store.dispatch(updateUniCourseSearchPayload({ payload }));
	}

	private _updatePayloadAndFetchPaginatedCourses(
		payload: Partial<UniversityCourseSearchPayload>,
	) {
		this._updatePayload(payload);
		this.isClearingView()
			? this._store.dispatch(fetchPaginatedClearingUniCourses())
			: this._store.dispatch(fetchPaginatedUniCourses());
	}

	private _updateUniCourseSearchPayload(searchTerm: string, degree: string) {
		const name = searchTerm?.replace(/-/g, ' ').toLowerCase();
		const degreeLevel = getDegreeLevelType(degree);
		return { name, degreeLevel };
	}

	private _navigateWithSearch(searchTerm: string, degree: string) {
		const { segments, queryParams = {} } = getRouteSegmentsAndQueryParams(
			this._router.url,
		);

		if (degree) {
			const degreeLevel = degree === 'clearing' ? CLE_UNI_SEARCH_SLUG : degree;
			segments.splice(1, 1, degreeLevel);
		}

		const search = searchTerm.trim().replace(/ /g, '-').toLowerCase();
		if (search) {
			queryParams['search'] = search;
		} else {
			delete queryParams['search'];
		}

		this._router.navigate([...segments], {
			queryParams: sortQueryParamsByKey(queryParams),
		});
	}

	private _initPayload() {
		const [min = 0, max = MAX_UCAS_TARIFF] = this.ucaspts()?.split('_') ?? [];
		const subject = this.formattedSubject() || null;
		const payload: Partial<UniversityCourseSearchPayload> = {
			slug: this.uni(),
			degreeLevel: getDegreeLevelType(this.studyLevel()) || null,
			studyMode: getStudyModeType(this.mode()) || null,
			name: subject,
			tariffMin: Number(min),
			tariffMax: Number(max),
			page: Number(this.page()) || 1,
			offset: Number(this.page()) > 1 ? Offset.Off : Offset.On,
		};
		this.updatePayloadAndFetchCourses(payload);
	}

	private _initDegreeLevel() {
		const degreeLevelOptions: DegreeLevelLabel[] = this.isClearingView()
			? ['Clearing', 'Undergraduate', 'Postgraduate']
			: ['Undergraduate', 'Postgraduate'];

		this.degreeLevelOptions = getDegreeLevelOptions(degreeLevelOptions);
		this._degreeLevelSrv.setDegreeLevels(this.degreeLevelOptions);
		const selectedDegreeLevel =
			this.degreeLevelOptions.find(
				({ link }) => link?.toLowerCase() === this.studyLevel()?.toLowerCase(),
			) ?? this.degreeLevelOptions[0];

		this._degreeLevelSrv.setSelectedDegreeLevel(selectedDegreeLevel);
	}

	private _removeInvalidQueryParams(
		queryParams: { [key: string]: string } | undefined,
	) {
		if (!queryParams) return;

		if (isPartTimeCourse(queryParams['mode'])) {
			delete queryParams['mode'];
		}

		if (!isValidClearingDegreeQueryParam(queryParams['degree'])) {
			delete queryParams['degree'];
		}
	}
}
